import { Children, isValidElement, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, Check } from "lucide-react";

const MAX_SHOWN = 100;

const textOf = (node) => {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (isValidElement(node)) return textOf(node.props.children);
  return "";
};

// Flattens <option>/<optgroup> children (through fragments and arrays) into
// [{value, label, disabled, group}] — so this is a drop-in for <select>.
function collectOptions(children, group = null, out = []) {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === "option") {
      const label = textOf(child.props.children);
      out.push({ value: child.props.value !== undefined ? String(child.props.value) : label, label, disabled: !!child.props.disabled, group });
    } else if (child.type === "optgroup") {
      collectOptions(child.props.children, child.props.label, out);
    } else if (child.props && child.props.children) {
      collectOptions(child.props.children, group, out);
    }
  });
  return out;
}

const matches = (label, q) => {
  const hay = label.toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
};

/**
 * Searchable dropdown, written as a drop-in for <select>: same <option> /
 * <optgroup> children, same `value`, and onChange receives `{ target: {
 * value } }`, so existing `onChange={(e) => set(e.target.value)}` code is
 * untouched. Type to filter. With `onSearch(q)`, the parent can also fetch
 * matching options from the server for very large lists.
 */

export default function SearchableSelect({ value, onChange, children, className = "", disabled = false, onSearch, placeholder = "Select…", ...rest }) {
  const testId = rest["data-testid"];
  const options = useMemo(() => collectOptions(children), [children]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const current = options.find((o) => o.value === String(value ?? ""));
  const shownLabel = current ? current.label : placeholder;
  const isPlaceholder = !current || current.value === "";

  const filtered = useMemo(() => (query.trim() ? options.filter((o) => matches(o.label, query)) : options), [options, query]);
  const visible = filtered.slice(0, MAX_SHOWN);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  useEffect(() => {
    if (!onSearch || !open) return;
    const t = setTimeout(() => onSearch(query), 250);
    return () => clearTimeout(t);
  }, [query, open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const openList = () => {
    if (disabled) return;
    setQuery("");
    const idx = options.findIndex((o) => o.value === String(value ?? ""));
    setActive(idx >= 0 && idx < MAX_SHOWN ? idx : 0);
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const pick = (o) => {
    if (!o || o.disabled) return;
    setOpen(false);
    if (o.value !== String(value ?? "")) onChange?.({ target: { value: o.value }, currentTarget: { value: o.value } });
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, visible.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(visible[active]); }
    else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
    else if (e.key === "Tab") setOpen(false);
  };

  // Keep the caller's sizing/spacing classes; drop native-select-only padding quirks.
  const buttonCls = `${className} flex items-center justify-between gap-2 text-left bg-white ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`;

  let lastGroup = null;
  return (
    <div className={`relative ${className.includes("w-full") || className.includes("flex-1") ? "w-full" : "inline-block"} ${className.match(/\b(flex-1|sm:col-span-\d|col-span-\d)\b/g)?.join(" ") || ""}`} ref={boxRef}>
      <button
        type="button" disabled={disabled} onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={(e) => { if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) { e.preventDefault(); openList(); } }}
        data-testid={testId} aria-haspopup="listbox" aria-expanded={open} className={buttonCls}
      >
        <span className={`truncate ${isPlaceholder ? "text-slate-400" : ""}`}>{shownLabel}</span>
        <ChevronDown size={14} className="shrink-0 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 min-w-full w-max max-w-[min(90vw,28rem)] rounded-lg border border-slate-200 bg-white shadow-lg" data-testid={testId ? `${testId}-menu` : undefined}>
          <div className="p-1.5 border-b border-slate-100 relative">
            <Search size={13} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onKeyDown}
              placeholder="Type to search…" data-testid={testId ? `${testId}-search` : undefined}
              className="w-full h-8 pl-7 pr-2 rounded-md text-[13px] outline-none bg-slate-50 focus:bg-white focus:ring-1 focus:ring-sky-300"
            />
          </div>
          <div ref={listRef} role="listbox" className="max-h-64 overflow-auto py-1 text-[13px]">
            {visible.map((o, i) => {
              const header = o.group && o.group !== lastGroup ? o.group : null;
              lastGroup = o.group;
              const selected = o.value === String(value ?? "");
              return (
                <div key={`${o.group || ""}-${o.value}-${i}`}>
                  {header && <div className="px-3 pt-2 pb-1 text-[10.5px] uppercase tracking-wide text-slate-400 font-semibold">{header}</div>}
                  <div
                    role="option" aria-selected={selected} data-index={i}
                    onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                    className={`px-3 py-2 flex items-center gap-2 ${o.disabled ? "text-slate-300 cursor-not-allowed" : "cursor-pointer"} ${i === active && !o.disabled ? "bg-sky-50" : ""} ${o.value === "" ? "text-slate-400" : ""}`}
                  >
                    <span className="flex-1 truncate">{o.label}</span>
                    {selected && <Check size={13} className="text-sky-600 shrink-0" />}
                  </div>
                </div>
              );
            })}
            {visible.length === 0 && <div className="px-3 py-2 text-slate-400">No match.</div>}
            {filtered.length > MAX_SHOWN && <div className="px-3 py-2 text-[11.5px] text-slate-400 border-t border-slate-100">Showing {MAX_SHOWN} of {filtered.length.toLocaleString()} — keep typing to narrow down.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
